import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Button, TextField, MenuItem, Select,
  FormControl, InputLabel, FormHelperText, Snackbar, Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import logo from '../../assets/images/logo.png'; // eslint-disable-line no-unused-vars
import PremiumHeader from '../../components/Header/PremiumHeader';
import { useLang, t } from '../../context/LanguageContext';
import T from '../../translations/index';
import Footer from '../../components/Footer/Footer';
import '../common.css';
import './hireus.css';

const BUDGET_RANGES = [
  'Below ₹10 Lakhs', '₹10–50 Lakhs', '₹50 Lakhs – ₹1 Crore',
  '₹1–5 Crore', '₹5 Crore+', 'Prefer not to disclose'
];

const initialForm = {
  companyName: '', contactPerson: '', email: '', phone: '',
  projectLocation: '', workType: '', budget: '',
  projectDescription: '', requiredStartDate: '',
  drawing: null,
};

const HireUs = () => {
  const navigate = useNavigate();
  const { lang } = useLang();

  const WORK_TYPES = lang === 'hi' ? [
    'बॉयलर कार्य', 'पाइपिंग कार्य (IBR और Non-IBR)', 'ESP कार्य',
    'यांत्रिक औद्योगिक संविदा', 'सिविल और संरचनात्मक कार्य',
    'क्षेत्र विकास और बागवानी', 'विद्युत कार्य',
    'परियोजना नियोजन और पर्यवेक्षण', 'अन्य'
  ] : [
    'Boiler Works', 'Piping Works (IBR & Non-IBR)', 'ESP Works',
    'Mechanical Industrial Contracting', 'Civil & Structural Works',
    'Area Development & Horticulture', 'Electrical Works',
    'Project Planning & Supervision', 'Other'
  ];

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // eslint-disable-next-line no-unused-vars
  const handleNavClick = (e, path) => {
    e.preventDefault();
    const routes = { home: '/', about: '/about', services: '/services', projects: '/projects', clients: '/clients', contact: '/contact', careers: '/careers', hireus: '/hireus' };
    navigate(routes[path] || '/');
  };

  const validate = () => {
    const e = {};
    if (!form.companyName.trim()) e.companyName = 'Company name is required';
    if (!form.contactPerson.trim()) e.contactPerson = 'Contact person name is required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email address';
    if (!/^[6-9]\d{9}$/.test(form.phone)) e.phone = 'Enter a valid 10-digit phone number';
    if (!form.projectLocation.trim()) e.projectLocation = 'Project location is required';
    if (!form.workType) e.workType = 'Please select type of work';
    if (!form.projectDescription.trim()) e.projectDescription = 'Please describe your project';
    return e;
  };

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleFile = (e) => {
    setForm(prev => ({ ...prev, drawing: e.target.files[0] || null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSubmitting(true);
    setSubmitError('');

    const fd = new FormData();
    fd.append('companyName', form.companyName);
    fd.append('contactPerson', form.contactPerson);
    fd.append('email', form.email);
    fd.append('phone', form.phone);
    fd.append('projectLocation', form.projectLocation);
    fd.append('workType', form.workType);
    fd.append('budget', form.budget || '');
    fd.append('projectDescription', form.projectDescription);
    fd.append('requiredStartDate', form.requiredStartDate || '');
    if (form.drawing) fd.append('drawing', form.drawing);

    try {
      const res = await fetch('/hireus_submit.php', { method: 'POST', body: fd });
      const data = await res.json();
      if (res.ok) {
        setSubmitted(true);
        setSnackOpen(true);
      } else {
        setSubmitError(data.error || 'Submission failed. Please try again.');
      }
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => { setForm(initialForm); setErrors({}); setSubmitted(false); setSubmitError(''); };

  if (submitted) {
    return (
      <div className="routed-page">
        <PremiumHeader />
        <div className="hireus-success-screen">
          <div className="hireus-success-card">
            <CheckCircleOutlineIcon className="hireus-success-icon" />
            <h2>{t(T.enquiryReceived, lang)}</h2>
            <p>{t(T.thankYouFrom, lang)}, <strong>{form.contactPerson}</strong> {t(T.from, lang)} <strong>{form.companyName}</strong>!</p>
            <p className="hireus-success-sub">{t(T.bizTeamContact, lang)} <strong>{form.email}</strong> {t(T.orPhone, lang)} <strong>{form.phone}</strong> {t(T.within24hrs, lang)}</p>
            <div className="hireus-detail-row">
              <span>{t(T.workTypeLabel, lang)}</span><span>{form.workType}</span>
            </div>
            <div className="hireus-detail-row">
              <span>{t(T.locationLabel, lang)}</span><span>{form.projectLocation}</span>
            </div>
            {form.requiredStartDate && (
              <div className="hireus-detail-row">
                <span>{t(T.startDateLabel, lang)}</span><span>{form.requiredStartDate}</span>
              </div>
            )}
            <div className="hireus-status-badge">{t(T.newEnquiry, lang)}</div>
            <div className="hireus-success-actions">
              <Button variant="outlined" onClick={handleReset} className="hireus-btn-outline">{t(T.submitAnother, lang)}</Button>
              <Button variant="contained" onClick={() => navigate('/')} className="hireus-btn-filled">{t(T.goToHome, lang)}</Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="routed-page">
      <PremiumHeader />

      {/* Hire Us Hero */}
      <section className="hireus-hero-banner">
        <div className="hireus-hero-bg" />
        <div className="hireus-hero-overlay" />
        <div className="hireus-hero-content">
          <div className="hireus-hero-eyebrow">Partner With Us</div>
          <h1 className="hireus-hero-title">Hire India's Most Trusted<br/><span>Thermal Power Contractor</span></h1>
          <p className="hireus-hero-sub">14+ years · 50+ projects · Pan-India operations · 500+ skilled workforce</p>
        </div>
      </section>

      {/* Capabilities */}
      <section className="hireus-cap-section">
        <div className="hireus-cap-inner">
          <div className="hireus-cap-eyebrow">Our Capabilities</div>
          <h2 className="hireus-cap-title">What We Deliver</h2>
          <p className="hireus-cap-sub">Comprehensive mechanical contracting services for thermal power, cement, and industrial infrastructure projects.</p>
          <div className="hireus-cap-grid">
            {[
              { icon: '🔥', name: 'Boiler Works', desc: 'Complete boiler erection, welding (IBR-compliant), pressure testing, insulation, and commissioning support.', tag: 'IBR Certified' },
              { icon: '🌀', name: 'ESP Erection', desc: 'Electrostatic precipitator erection, alignment, high-voltage transformer installation, and testing.', tag: 'Complete Scope' },
              { icon: '⚙️', name: 'Coal Handling', desc: 'Belt conveyors, crushers, screeners, feeders, wagon tippers, stacker-reclaimers — full CHP scope.', tag: 'Full CHP' },
              { icon: '💧', name: 'Ash Management', desc: 'Fly ash handling, bottom ash systems, ESP hopper & pipes, slurry pumping, and ash dyke works.', tag: 'Zero Waste' },
              { icon: '🌡️', name: 'Cooling Tower', desc: 'Cooling tower civil, mechanical, and structural works including fill media, drift eliminators, and basin.', tag: 'All Types' },
              { icon: '🌿', name: 'FGD Systems', desc: 'Flue Gas Desulfurization for SO₂ compliance — absorber, spray system, reagent preparation & lime feeding.', tag: 'Emission Compliant' },
              { icon: '🏗️', name: 'Structural Works', desc: 'Steel structure fabrication, erection, grouting, and alignment for TG building, ESP and auxiliary structures.', tag: 'Design-to-Erect' },
              { icon: '🛠️', name: 'Shutdown / AMC', desc: 'Planned and emergency shutdown works, annual maintenance contracts, inspection, and life extension services.', tag: '24hr Response' },
              { icon: '🌍', name: 'Area Development', desc: 'Horticulture, internal roads, drainage, compound walls, and township development for new greenfield projects.', tag: 'Greenfield Ready' },
            ].map((cap, i) => (
              <div className="hireus-cap-card" key={i}>
                <span className="hireus-cap-icon">{cap.icon}</span>
                <div className="hireus-cap-name">{cap.name}</div>
                <p className="hireus-cap-desc">{cap.desc}</p>
                <span className="hireus-cap-tag">{cap.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="page-container content-wrapper">
        <div className="back-button-container">
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>{t(T.back, lang)}</Button>
        </div>

        <div className="hireus-form-card">
          <form onSubmit={handleSubmit} noValidate>

            <div className="form-section-label"><span className="form-section-dot hireus-dot" />{t(T.companyContactDetails, lang)}</div>
            <div className="form-grid-2">
              <TextField label={t(T.companyNameLabel, lang)} value={form.companyName} onChange={handleChange('companyName')}
                error={!!errors.companyName} helperText={errors.companyName} fullWidth variant="outlined" className="hireus-field" />
              <TextField label={t(T.contactPersonLabel, lang)} value={form.contactPerson} onChange={handleChange('contactPerson')}
                error={!!errors.contactPerson} helperText={errors.contactPerson} fullWidth variant="outlined" className="hireus-field" />
            </div>
            <div className="form-grid-2">
              <TextField label={t(T.emailAddressLabel, lang)} value={form.email} onChange={handleChange('email')}
                error={!!errors.email} helperText={errors.email} fullWidth variant="outlined" className="hireus-field" />
              <TextField label={t(T.phoneNumberLabel, lang)} value={form.phone} onChange={handleChange('phone')}
                error={!!errors.phone} helperText={errors.phone} fullWidth variant="outlined" inputProps={{ maxLength: 10 }} className="hireus-field" />
            </div>

            <div className="form-section-label"><span className="form-section-dot hireus-dot" />{t(T.projectDetailsSection, lang)}</div>
            <div className="form-grid-2">
              <TextField label={t(T.projectLocationLabel, lang)} value={form.projectLocation} onChange={handleChange('projectLocation')}
                error={!!errors.projectLocation} helperText={errors.projectLocation} fullWidth variant="outlined" className="hireus-field" />
              <FormControl fullWidth error={!!errors.workType} className="hireus-field">
                <InputLabel>{t(T.typeOfWorkLabel, lang)}</InputLabel>
                <Select value={form.workType} onChange={handleChange('workType')} label={t(T.typeOfWorkLabel, lang)}>
                  {WORK_TYPES.map(wt => <MenuItem key={wt} value={wt}>{wt}</MenuItem>)}
                </Select>
                {errors.workType && <FormHelperText>{errors.workType}</FormHelperText>}
              </FormControl>
            </div>
            <div className="form-grid-2">
              <FormControl fullWidth className="hireus-field">
                <InputLabel>{t(T.projectBudgetLabel, lang)}</InputLabel>
                <Select value={form.budget} onChange={handleChange('budget')} label={t(T.projectBudgetLabel, lang)}>
                  {BUDGET_RANGES.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label={t(T.requiredStartDate, lang)} value={form.requiredStartDate} onChange={handleChange('requiredStartDate')}
                fullWidth variant="outlined" type="date" className="hireus-field"
                InputLabelProps={{ shrink: true }} />
            </div>
            <TextField
              label={t(T.projectDescLabel, lang)}
              value={form.projectDescription} onChange={handleChange('projectDescription')}
              error={!!errors.projectDescription} helperText={errors.projectDescription}
              fullWidth multiline rows={5} variant="outlined" className="hireus-field hireus-textarea"
              placeholder={t(T.projectDescPlaceholder, lang)} />

            <div className="form-section-label"><span className="form-section-dot hireus-dot" />{t(T.uploadDrawing, lang)}</div>
            <div className="hireus-upload-box">
              <label className="hireus-upload-label">
                <span className="hireus-upload-icon">📐</span>
                <div className="hireus-upload-text">
                  <span className="hireus-upload-title">{t(T.uploadDrawingTitle, lang)}</span>
                  <span className="hireus-upload-hint">{t(T.uploadDrawingHint, lang)}</span>
                </div>
                <input type="file" accept=".pdf,.dwg,.jpg,.jpeg,.png" onChange={handleFile} className="upload-input" />
                {form.drawing && (
                  <span className="hireus-upload-filename">📎 {form.drawing.name}</span>
                )}
              </label>
            </div>

            {/* Why choose us strip */}
            <div className="hireus-trust-row">
              {[
                { icon: '✅', textKey: 'yearsExp' },
                { icon: '🏆', textKey: 'projectsDelivered' },
                { icon: '🔒', textKey: 'ndaRequest' },
                { icon: '⚡', textKey: 'hrResponse' },
              ].map(({ icon, textKey }) => (
                <div className="hireus-trust-chip" key={textKey}>{icon} {t(T[textKey], lang)}</div>
              ))}
            </div>

            <div className="form-submit-row">
              <Button type="submit" variant="contained" className="hireus-submit-btn" size="large" disabled={submitting}>
                {submitting ? t(T.sendingBtn, lang) : t(T.sendBusinessEnquiry, lang)}
              </Button>
              {submitError && <p className="form-error-msg" style={{color:'#d32f2f',marginTop:'8px',fontSize:'0.9rem'}}>⚠️ {submitError}</p>}
              <p className="form-note">{t(T.urgentCall, lang)} <a href="tel:+916200247745">+91 6200247745</a></p>
            </div>
          </form>
        </div>
      </div>

      <Footer />

      <Snackbar open={snackOpen} autoHideDuration={4000} onClose={() => setSnackOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setSnackOpen(false)}>
          {t(T.enquirySentSnack, lang)}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default HireUs;
